/**
 * Capability Service (Frontend Singleton)
 *
 * Manages the current tenant's capabilities and tier in memory.
 * Initialized during app boot alongside PermissionService.
 *
 * Capabilities act as a "ceiling" above RBAC — they determine which feature
 * areas are available to the account. RBAC then controls what individual
 * users can do within those areas.
 *
 * PD-16917: the rule is a strict whitelist. A tenant holds exactly the
 * capabilities the API returned; an empty list means no gated area at all,
 * and a list that has not loaded yet allows nothing. There is no "not loaded
 * means everything" fallback any more.
 */

import type {
	CapabilityAlias,
	CapabilityTierAlias,
} from '../constants/capabilities';

class CapabilityServiceClass {
	private capabilities: Set<string> = new Set();
	private tier: string | null = null;
	private loaded = false;

	/**
	 * Initialize capabilities on application load.
	 * Called after fetching capabilities from GET /api/system/capabilities.
	 */
	initialize(capabilities: Array<string>, tier?: string | null): void {
		this.capabilities = new Set(capabilities);
		this.tier = tier ?? null;
		this.loaded = true;
	}

	/**
	 * Check if the tenant has a specific capability.
	 */
	hasCapability(capability: string | CapabilityAlias): boolean {
		return this.capabilities.has(capability);
	}

	/**
	 * Check if the tenant has any of the specified capabilities (OR logic).
	 */
	hasAnyCapability(capabilities: ReadonlyArray<string | CapabilityAlias>): boolean {
		return capabilities.some((cap) => this.capabilities.has(cap));
	}

	/**
	 * Check if the tenant has all of the specified capabilities (AND logic).
	 */
	hasAllCapabilities(
		capabilities: ReadonlyArray<string | CapabilityAlias>,
	): boolean {
		return capabilities.every((cap) => this.capabilities.has(cap));
	}

	/**
	 * The one gating rule for UI: true when the tenant holds the capability,
	 * or ANY of them when given a list. Strict — false before the list loads.
	 */
	allows(
		capability: string | CapabilityAlias | ReadonlyArray<string | CapabilityAlias>,
	): boolean {
		if (!this.loaded) {
			return false;
		}

		if (typeof capability === 'string') {
			return this.capabilities.has(capability);
		}

		return this.hasAnyCapability(capability);
	}

	/**
	 * Get the tenant's current tier alias.
	 */
	getTier(): string | null {
		return this.tier;
	}

	/**
	 * Check if the tenant is on a specific tier.
	 */
	isTier(tierAlias: string | CapabilityTierAlias): boolean {
		return this.tier === tierAlias;
	}

	/**
	 * Get all capabilities as an array.
	 */
	getCapabilities(): Array<string> {
		return Array.from(this.capabilities);
	}

	/**
	 * Check if capabilities have been loaded from the API. An empty list that
	 * did load counts as loaded: that tenant simply holds nothing.
	 */
	isInitialized(): boolean {
		return this.loaded;
	}

	/**
	 * Clear all capabilities and tier.
	 */
	clear(): void {
		this.capabilities.clear();
		this.tier = null;
		this.loaded = false;
	}
}

// Export singleton instance (same pattern as PermissionService)
export const CapabilityService = new CapabilityServiceClass();
